import * as React from 'react'

import { Box, Href, Text, useClassNames } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { dateToString } from 'modules/core/lib/dates'

import { useComplaint } from '../../complaintContext'
import * as styles from './index.vanilla.css'
import type { Element as FeedElement } from './useFeed'
import useFeed from './useFeed'

interface ElementProps {
  element: FeedElement
  isLast: boolean
}

const Element = ({ element, isLast }: ElementProps) => {
  const cx = useClassNames(styles)
  const { complaintMessageFiles } = useComplaint()

  const elementContent: React.ReactNode | null | undefined = (() => {
    if (!element.isMessage) return null

    const files = complaintMessageFiles.filter({
      message_id: element.message?.id,
    })

    return (
      <Box
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        borderRadius={{
          all: 'abs008',
        }}
        paddingY="s16"
        paddingX="s24"
        gap="s8"
      >
        {element.text && (
          <Text.Normal color="grey800" size="300">
            <>{element.text}</>
          </Text.Normal>
        )}
        {files.length > 0 && (
          <Box gap="s2">
            {files.map((file) => (
              <Href key={file.attributes.id} href={file.path} target="_blank" download={file.name}>
                <Text.Normal size="200" ellipsis color="radicalRed1000" weight="semibold">
                  {file.name}
                </Text.Normal>
              </Href>
            ))}
          </Box>
        )}
      </Box>
    )
  })()

  return (
    <div
      className={cx('element', {
        lastElement: isLast,
      })}
    >
      <Box gap="s4">
        <Text.Normal size="200" color="grey800">
          {dateToString(element.date, 'ui_short_with_time')}
        </Text.Normal>
        <Text.Normal size="300" color="grey1000">
          {element.title}
        </Text.Normal>
        {elementContent}
      </Box>
    </div>
  )
}

const Feed = () => {
  const cx = useClassNames(styles)
  const { complaintMessages, complaintMessageFiles, complaint } = useComplaint()

  useAsyncData(async () => await complaintMessageFiles.fetch(), [complaintMessages.length])

  if (!complaint) throw new Error('complaint not found')

  const feed = useFeed(complaintMessages, complaint).get()

  return (
    <div className={cx('feed')}>
      <div className={cx('bar')} />
      {feed.map((element, index) => (
        <Element element={element} isLast={index === feed.length - 1} key={element.id} />
      ))}
    </div>
  )
}

export default observer(Feed)
